import type { SignalRow } from '@shared/flow/signal-view';
import { GONG_VERIFICATION_FRESH_MS, resolveSignalVerificationState } from '@shared/flow/signal-verification';

/**
 * Extracted from `SignalsToolbar.tsx` (bead .18, S4-U2) so the V2
 * `SignalsToolbarActions` (`shell-next/tables/SignalsToolbarActions.tsx`)
 * can share the exact same amber-dot rule without a second copy. Pure —
 * Glaze-free and React-free, per the coding standards' layer boundary for
 * `signals/*.ts` helpers. V1 `SignalsToolbar.tsx` imports these back
 * (behaviour-neutral move).
 *
 * R9/R10, rebuilt on the resolver (fix-plan-batch2.md D-A / F1′): a loaded
 * row counts toward the amber dot ("Gong hasn't confirmed N tasks in the
 * last 2 hours" — the same vocabulary as the Signal card's Gong status line,
 * qa-batch4-plan.md D-B) exactly when
 * `resolveSignalVerificationState` says `unverified` — no whole, fresh probe
 * observation for a row that would otherwise be live. The old test
 * (`signal_status === 'Running' && gong_verified_at === null`) mixed the
 * snapshot's status with the probe's timestamp: a Running row whose probe
 * aged past the 2 h window was unverified to the resolver but invisible to
 * the dot, and a row the probe says is Removed from flow under a stale
 * Running snapshot is verified (Archive only), so it must not light it.
 * Closed/snoozed/no-flow/removed/paused rows never count.
 */
export function unverifiedRunningCount(rows: SignalRow[], now: Date): number {
  return rows.filter((row) => resolveSignalVerificationState(row, now) === 'unverified').length;
}

/** The freshness window in whole hours — derived, so the tooltip can never drift from the resolver's rule. */
export const FRESH_WINDOW_HOURS = Math.round(GONG_VERIFICATION_FRESH_MS / 3_600_000);

/** The amber dot's tooltip (qa-batch4-plan.md D-B) — singular/plural on the task count. */
export function unverifiedDotTitle(n: number): string {
  return `Gong hasn't confirmed ${n} ${n === 1 ? 'task' : 'tasks'} in the last ${FRESH_WINDOW_HOURS} hours — press Refresh to recheck`;
}
